import { assert, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import fromPairs from "lodash/fromPairs";
import startCase from "lodash/startCase";
import upperFirst from "lodash/upperFirst";
import type {
    ActionPropertyDescriptor,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    ComponentSpecialCaseDescriptor,
    EditedColumnsAndTables,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    PropertyTableGetter,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
} from ".";
import { combineEditedColumnsAndTables, isNativeTableInAppDescriptionContext } from ".";
import type { DeserializationSpec } from "../../common/components/computation-types";
import { getLocalizedString } from "../../common/components/localization";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import type { UIClass } from "../../common/components/types";
import { ReactUI } from "../../common/components/uis";
import type { LoadedColumnValues, LoadedRow, LoadingValue, Row } from "../../common/computation-model/data";
import { asPrimitive, isLoadingValue, isPrimitive } from "../../common/computation-model/data";
import type { TableName } from "../../common/database-strings";
import type {
    ColumnAssignmentsDescription,
    ComponentDescription,
    ContainerComponentDescription,
    Description,
    InputOutputTables,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    ActionKind,
    ArrayScreenFormat,
    getAllowedFormTables,
    getPrimitiveColumns,
    getTableName,
    isPrimitiveType,
    makeEmptyComponentDescription,
    makeInputOutputTables,
    MutatingScreenKind,
    PropertyKind,
    sheetNameForTable,
} from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { getFeatureSetting } from "../../common/feature-settings";
import { makeRowID } from "../../common/make-row-id";
import { isValidEmailAddress, mapFilterUndefinedAsync } from "../../common/Support";
import type { WireButtonComponent } from "../../fluent-components/fluent-components";
import type {
    ArrayContentDescriptionForProperties,
    DescriptionOfComponent,
    FluentArrayContent,
    FluentComponent,
    FluentComponentSpec,
} from "../../fluent-components/fluent-components-spec";
import { ContainerKind } from "../../fluent-components/fluent-components-spec";
import type { ComponentOrArrayDescription, FluentPropertiesSpec } from "../../fluent-components/fluent-properties-spec";
import { makeActionDescriptors, makePropertyDescriptors } from "../../fluent-components/fluent-properties-spec";
import { WireComponentKind } from "../../wire/component-kind";
import type { WireAction, WireComponent, WireEditableValue, WirePrimitiveValueWithType } from "../../wire/types";
import { UIBackgroundStyle, UIButtonAppearance } from "../../wire/types";
import type { ArrayContentHandler } from "../array-screens/array-content";
import { ArrayContentHandlerBase } from "../array-screens/array-content";
import { getColumnAssignments } from "../description-utils";
import { duplicateComponent, handlerForComponentKind } from "../handlers";
import { editComponentsForTable } from "../make-edit-screen";
import type { OutputValueGetters, WireStringGetter } from "../wire/utils";
import {
    hydrateAction,
    inflateActions,
    inflateColumnAssignments,
    inflateComponent,
    isNotEmptyDisplayValue,
    registerActionRunner,
    spreadComponentID,
} from "../wire/utils";
import type { ComponentHandler } from "./component-handler";
import { PopulationMode } from "./description-handlers";
import type { PropertyDescriptorsWithColumns } from "./descriptor-utils";
import { makePropertyDescriptorsForColumns } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { populateDescription } from "./populate-description";
import { getDefaultPrimitiveActionKinds } from "./primitive";
import { ColumnPropertyFlag, ColumnPropertyHandler, TablePropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getActionProperty,
    getArrayProperty,
    getTableProperty,
    makeEnumProperty,
    makeStringProperty,
} from "./property-utils";
import type {
    WireActionHydrator,
    WireActionRunner,
    WireInflationBackend,
    WireRowComponentHydrationBackend,
    WireRowComponentHydrator,
    WireRowComponentInflationBackend,
    WireRowComponentValueGetter,
    WireRowHydrationValueProvider,
    WireTableComponentHydrator,
} from "./wire";

// Returns [value, is displayed non-empty]
type ValueGetter = (hb: WireRowHydrationValueProvider) => [unknown, boolean];

interface Getters {
    readonly valueGetters: readonly (readonly [name: string, getter: ValueGetter])[];
    readonly arrayBuilders: readonly (readonly [
        string,
        (hb: WireRowComponentHydrationBackend, keyPrefix: string) => readonly [readonly unknown[], boolean]
    ])[];
    readonly actionHydrators: readonly (readonly [string, WireActionHydrator])[];
}

function makeGetters(
    ib: WireInflationBackend,
    { propertySpecs, arraySpecs, actionSpecs }: FluentPropertiesSpec,
    desc: Description,
    parentDesc: ComponentOrArrayDescription | undefined
): Getters {
    const valueGetters = propertySpecs.map(p => {
        const name = p.argumentSpec[0];

        if (p.when?.(desc as any, parentDesc as any) === false) {
            const getter: ValueGetter = () => [undefined, false];
            return [name, getter] as const;
        }

        const [getter, type, isDisplayed] = p.getValueGetter(desc, ib);
        let valueGetter: ValueGetter;
        if (p.withType && type !== undefined) {
            assert(isPrimitiveType(type));
            valueGetter = hb => {
                const v = getter(hb);
                if (v === null) return [null, false];
                if (isLoadingValue(v) || !isPrimitive(v)) return [undefined, false];
                const converted = asPrimitive(p.converter(v));
                const result: WirePrimitiveValueWithType = {
                    value: converted,
                    typeKind: type.kind,
                };
                return [result, isDisplayed && isNotEmptyDisplayValue(converted)];
            };
        } else {
            valueGetter = hb => {
                const v = getter(hb);
                if (v === null) return [null, false];
                if (isLoadingValue(v)) return [undefined, false];
                const converted = p.converter(v);
                return [converted, isDisplayed && isNotEmptyDisplayValue(converted)];
            };
        }
        return [name, valueGetter] as const;
    });

    const arrayBuilders = arraySpecs.map((a, i) => {
        const arr: readonly Description[] = getArrayProperty((desc as any)[a.name]) ?? [];
        const itemGetters = arr.map(item => {
            const copy = { ...item };
            for (const [k, toRemove] of Object.entries(a.removeIfMissing)) {
                if ((item as any)[k] !== undefined) continue;
                for (const r of toRemove) {
                    delete (copy as any)[r];
                }
            }
            return makeGetters(ib, a.itemsSpec, copy, desc as any);
        });
        return [
            a.name,
            (hb: WireRowComponentHydrationBackend, keyPrefix: string) => {
                const itemsAndFlags = itemGetters.map(getters =>
                    hydrateGetters(getters, hb, `${keyPrefix}array[${i}].`)
                );
                const items = itemsAndFlags.map(([item]) => item);
                const someDisplayed = itemsAndFlags.some(([, f]) => f);
                return [items, someDisplayed] as const;
            },
        ] as const;
    });

    const actionHydrators = mapFilterUndefined(actionSpecs ?? [], a => {
        const name = a.argumentSpec[0];
        const actionDesc = getActionProperty((desc as any)[name]);
        if (actionDesc === undefined) return undefined;

        const runner = inflateActions(ib, [actionDesc]);
        if (runner === undefined) return undefined;

        return [name, runner] as const;
    });

    return { valueGetters, arrayBuilders, actionHydrators };
}

// Returns [properties, has any value]
function hydrateGetters<T>(
    { valueGetters, arrayBuilders, actionHydrators }: Getters,
    hb: WireRowComponentHydrationBackend,
    keyPrefix: string
): [T, boolean] {
    let isDisplayedNonEmpty = false;

    const hydrated = {
        ...fromPairs(
            valueGetters.map(([k, g]) => {
                const [v, f] = g(hb);
                if (f) {
                    isDisplayedNonEmpty = true;
                }
                return [k, v];
            })
        ),
        ...fromPairs(
            arrayBuilders.map(([k, b], i) => {
                const [v, f] = b(hb, `${keyPrefix}array[${i}]`);
                if (f) {
                    isDisplayedNonEmpty = true;
                }
                return [k, v];
            })
        ),
        ...fromPairs(
            mapFilterUndefined(actionHydrators, ([k, h], i) => {
                // If an action isn't loaded yet then we don't add it, which
                // means it can't be triggered in the UI until it's loaded.
                const maybeAction = registerActionRunner(hb, `${keyPrefix}action[${i}]`, () =>
                    hydrateAction(h, hb, false)
                );
                if (maybeAction === undefined) return undefined;
                isDisplayedNonEmpty = true;
                return [k, maybeAction];
            })
        ),
    } as any;
    return [hydrated, isDisplayedNonEmpty];
}

export type ComponentInflator<T> = (
    ib: WireRowComponentInflationBackend,
    desc: DescriptionOfComponent<T>
) => WireRowComponentHydrator | undefined;

const targetTablePropertyHandler = new TablePropertyHandler(
    "targetTable",
    "Target table",
    true,
    (_desc, schema) => getAllowedFormTables(schema),
    PropertySection.Data
);

function getTargetTableName(desc: Description | undefined): TableName | undefined {
    if (desc === undefined) return undefined;
    return targetTablePropertyHandler.getTableName(desc);
}

function getTargetTable(_tables: InputOutputTables, desc: Description, schema: SchemaInspector) {
    return targetTablePropertyHandler.lookupTable(desc, schema);
}

function makeOnSubmitActionDescriptor(
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined
): ActionPropertyDescriptor {
    return {
        kind: PropertyKind.Action,
        property: { name: "onSubmitAction" },
        label: "On submit action",
        kinds: getDefaultPrimitiveActionKinds(schema, mutatingScreenKind),
        getIndirectTable: getTargetTable,
        section: { name: "After submit action", order: 1 },
        defaultAction: () => ({ kind: ActionKind.ShowToast }),
    };
}

function makeAdditionalColumnAssignmentPropertyDescriptors<TDesc extends FormComponentDescription>(
    tables: InputOutputTables,
    adc: AppDescriptionContext,
    excludeOutputColumns: ReadonlySet<string>
): PropertyDescriptorsWithColumns {
    const isNative = isNativeTableInAppDescriptionContext(adc, getTableName(tables.output));
    return makePropertyDescriptorsForColumns<TDesc>(
        tables,
        adc.appKind,
        getColumnAssignments,
        columns => ({ columnAssignments: columns } as Partial<TDesc>),
        {
            withActionSource: false,
            withClearColumn: false,
            allowUserSpecificColumns: isNative,
            allowCustomAndUserProfile: true,
            emptyByDefault: true,
            propertySection: { name: "Additional columns", order: 0 },
            excludeOutputColumns,
        }
    );
}

function makeTargetPropertyDescriptors(
    targetTableName: TableName | undefined,
    inputTable: TableGlideType | undefined,
    adc: AppDescriptionContext,
    excludeOutputColumns: ReadonlySet<string>
): PropertyDescriptorsWithColumns {
    const targetTable = adc.findTable(targetTableName);
    if (targetTableName === undefined || inputTable === undefined) return [[], []];

    return makeAdditionalColumnAssignmentPropertyDescriptors(
        makeInputOutputTables(inputTable, targetTable),
        adc,
        excludeOutputColumns
    );
}

function inflateColumnAssignmentsFromDescription(
    ib: WireInflationBackend,
    desc: ColumnAssignmentsDescription,
    targetTableName: TableName,
    excludeOutputColumns: ReadonlySet<string>
): OutputValueGetters | undefined {
    const [, targetColumns] = makeTargetPropertyDescriptors(
        targetTableName,
        ib.tables.input,
        ib.adc,
        excludeOutputColumns
    );
    const targetColumnNames = new Set(targetColumns.map(c => c.name));
    const columnAssignments = getColumnAssignments(desc).filter(a => targetColumnNames.has(a.destColumn));
    return inflateColumnAssignments(ib, targetTableName, columnAssignments, true);
}

function hydrateOutputValueGetters(
    hb: WireRowComponentHydrationBackend,
    getters: OutputValueGetters
): LoadedColumnValues | LoadingValue {
    let loadingValue: LoadingValue | undefined;
    const result = fromPairs(
        mapFilterUndefined(getters, ([n, g]) => {
            const v = g(hb);
            if (v === null) return undefined;
            if (isLoadingValue(v)) {
                loadingValue = v;
                return undefined;
            }
            return [n, v];
        })
    );
    if (loadingValue !== undefined) return loadingValue;
    return result;
}

function getEditedColumnsInFormComponents(
    components: readonly ComponentDescription[],
    tables: InputOutputTables,
    adc: AppDescriptionContext
): ReadonlySet<string> {
    const columnNames = new Set<string>();
    for (const c of components) {
        const handler = handlerForComponentKind(c.kind);
        if (handler === undefined) continue;
        const editedColumns = handler.getEditedColumns(c, tables, adc, MutatingScreenKind.FormScreen, false);
        for (const e of editedColumns.editedColumns) {
            const [columnName, editsInScreenContext] = e;
            if (!editsInScreenContext) continue;
            columnNames.add(columnName);
        }
    }
    return columnNames;
}

function makeFormComponents(formTable: TableGlideType, appKind: AppKind): readonly ComponentDescription[] {
    // We put a maximum of 5 components in.
    const components = editComponentsForTable(formTable, appKind, MutatingScreenKind.FormScreen).slice(0, 5);

    const title: ComponentDescription = {
        ...makeEmptyComponentDescription(WireComponentKind.Hero),
        title: makeStringProperty(`Add to ${sheetNameForTable(formTable)}`),
        bgStyle: makeEnumProperty(UIBackgroundStyle.None),
    } as ComponentDescription;

    return [title, ...components];
}

function removeLoadingValues(row: Row): LoadedRow {
    return fromPairs(Object.entries(row).filter(([, v]) => !isLoadingValue(v))) as LoadedRow;
}

function makeComponentHandler<TDesc extends ComponentDescription>(
    spec: FluentComponentSpec,
    inflator: ComponentInflator<TDesc> | undefined
): ComponentHandler<TDesc> {
    const {
        properties: { propertySpecs, arraySpecs, actionSpecs },
        displayName,
        kind,
        group = "Layout",
        description = "No description",
        icon = "componentTitle",
        containerKind,
        docURL,
        featureSetting,
        specialCases,
        includeRegularComponent,
    } = spec;
    assert(displayName !== undefined && kind !== undefined);
    let formTablePropertyDescriptor: PropertyDescriptor | undefined;
    if (containerKind === ContainerKind.Form) {
        formTablePropertyDescriptor = targetTablePropertyHandler;
    }

    function asFormComponentDescription(desc: Description): FormComponentDescription {
        // TODO: It's ugly that we have to do this cast.  The only way
        // I know to make sure we have this property here is to add an
        // additional `TExtraDesc` argument to the
        // `FluentComponentSpec` to specify additional arguments that
        // make it into the description.
        return desc as unknown as FormComponentDescription;
    }

    class Handler extends ComponentHandlerBase<TDesc> {
        constructor() {
            super(kind);
        }

        // TODO: Implement `needsColumns` if/when we need to support Add
        // screens, or any other context where we don't have input columns.

        public get appKinds(): AppKind | "both" {
            return AppKind.Page;
        }

        protected get configuresScreenTitle(): boolean {
            return spec.configuresScreenTitle === true;
        }

        public getSubComponents(desc: TDesc): readonly ComponentDescription[] | undefined {
            if (containerKind === undefined) return undefined;
            // TODO: This is really ugly.
            return (desc as unknown as ContainerComponentDescription).components ?? [];
        }

        public getSubComponentTables(
            desc: TDesc,
            containingTables: InputOutputTables,
            schema: SchemaInspector,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): [InputOutputTables, MutatingScreenKind | undefined] | undefined {
            if (containerKind === undefined) return undefined;

            if (formTablePropertyDescriptor === undefined) {
                return [containingTables, mutatingScreenKind];
            }

            const targetTableName = targetTablePropertyHandler.getTableName(desc);
            const targetTable = schema.findTable(targetTableName);
            if (targetTable === undefined) return undefined;

            return [makeInputOutputTables(containingTables.input, targetTable), MutatingScreenKind.FormScreen];
        }

        public getDescriptor(
            desc: TDesc | undefined,
            table: TableGlideType | undefined,
            adc: AppDescriptionContext,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): ComponentDescriptor {
            const properties = makePropertyDescriptors(
                desc,
                undefined,
                [...propertySpecs, ...arraySpecs],
                undefined,
                adc,
                mutatingScreenKind,
                false,
                false
            );
            if (formTablePropertyDescriptor !== undefined) {
                properties.push(formTablePropertyDescriptor);
                const formTable = definedMap(desc, d => adc.findTable(targetTablePropertyHandler.getTableName(d)));
                if (table !== undefined && formTable !== undefined) {
                    const tables = makeInputOutputTables(table, formTable);
                    let editedColumns: ReadonlySet<string> | undefined;
                    if (desc !== undefined) {
                        editedColumns = getEditedColumnsInFormComponents(
                            this.getSubComponents(desc) ?? [],
                            tables,
                            adc
                        );
                    }
                    const [assignmentDescriptors] = makeAdditionalColumnAssignmentPropertyDescriptors(
                        tables,
                        adc,
                        editedColumns ?? new Set()
                    );
                    properties.push(...assignmentDescriptors);
                }
            }
            return {
                name: defined(displayName),
                description,
                img: icon,
                group,
                helpUrl: getDocURL(docURL ?? "button"),
                isLegacy:
                    !includeRegularComponent || (featureSetting !== undefined && !getFeatureSetting(featureSetting)),
                properties: [...properties, ...this.getBasePropertyDescriptors()],
                configuresScreenTitle: false,
            };
        }

        public getSpecialCaseDescriptors(): readonly ComponentSpecialCaseDescriptor[] {
            return specialCases?.map(([c]) => c) ?? [];
        }

        public getActionDescriptors(
            desc: TDesc,
            _table: TableGlideType | undefined,
            schema: SchemaInspector | undefined,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): readonly ActionPropertyDescriptor[] {
            const descrs = makeActionDescriptors(
                desc,
                undefined,
                actionSpecs,
                formTablePropertyDescriptor !== undefined ? getTargetTable : undefined,
                schema,
                mutatingScreenKind,
                false
            );
            if (formTablePropertyDescriptor !== undefined) {
                descrs.push(makeOnSubmitActionDescriptor(schema, MutatingScreenKind.FormScreen));
            }
            return descrs;
        }

        public getEditedColumns(
            desc: TDesc,
            tables: InputOutputTables,
            adc: AppDescriptionContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            withActions: boolean
        ): EditedColumnsAndTables {
            const base = super.getEditedColumns(desc, tables, adc, mutatingScreenKind, withActions);

            const formDesc = asFormComponentDescription(desc);

            const tableName = getTableProperty(formDesc.targetTable);
            if (tableName === undefined) return base;

            const columnAssignments = getColumnAssignments(formDesc);
            return combineEditedColumnsAndTables(base, {
                editedColumns: columnAssignments.map(a => [a.destColumn, false, true, tableName] as const),
                deletedTables: [],
            });
        }

        public async duplicateComponent(
            desc: TDesc,
            copyTables: InputOutputTables,
            iccc: InteractiveComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            screensCreated: Set<string>
        ): Promise<TDesc> {
            const base = await super.duplicateComponent(desc, copyTables, iccc, mutatingScreenKind, screensCreated);
            const oldComponents = this.getSubComponents(desc);
            if (oldComponents === undefined) {
                return base;
            }

            const subComponentTables = this.getSubComponentTables(desc, copyTables, iccc, mutatingScreenKind);
            if (subComponentTables === undefined) {
                return { ...base, components: [] };
            }

            const [subTables, subMutatingScreenKind] = subComponentTables;
            const components = await mapFilterUndefinedAsync(oldComponents, c =>
                duplicateComponent(c, subTables, iccc, subMutatingScreenKind, screensCreated)
            );
            return { ...base, components } as TDesc;
        }

        public rewriteAfterReload(
            desc: TDesc,
            tables: InputOutputTables,
            ccc: RewritingComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            isRewrite: boolean
        ): TDesc | undefined {
            const newDesc = super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind, isRewrite);
            if (newDesc === undefined) return undefined;
            const oldComponents = this.getSubComponents(desc);
            if (oldComponents === undefined) {
                return newDesc;
            }

            const components = mapFilterUndefined(oldComponents ?? [], c => {
                const handler = handlerForComponentKind(c.kind);
                if (handler === undefined) return undefined;
                return handler.rewriteAfterReload(c, tables, ccc, mutatingScreenKind, isRewrite);
            });

            return { ...newDesc, components } as TDesc;
        }

        public newComponent(
            tables: InputOutputTables,
            usedColumns: ReadonlySet<TableColumn>,
            editedColumns: ReadonlySet<TableColumn>,
            iccc: InteractiveComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): TDesc | undefined {
            let desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
            if (desc === undefined) return undefined;

            if (formTablePropertyDescriptor !== undefined) {
                const formTables = this.getSubComponentTables(desc, tables, iccc, mutatingScreenKind)?.[0];
                if (formTables !== undefined) {
                    desc = { ...desc, components: makeFormComponents(formTables.output, iccc.appKind) };
                }
            }

            return desc;
        }

        public newSpecialCaseComponent(
            specialCaseDescriptor: ComponentSpecialCaseDescriptor,
            tables: InputOutputTables,
            usedProperties: ReadonlySet<TableColumn>,
            editedProperties: ReadonlySet<TableColumn>,
            iccc: InteractiveComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            insideContainer: boolean
        ): TDesc | undefined {
            assert(specialCases !== undefined);

            const desc = this.newComponent(tables, usedProperties, editedProperties, iccc, mutatingScreenKind);
            if (desc === undefined) return undefined;

            for (const [specialCase, f] of specialCases) {
                if (specialCaseDescriptor === specialCase) {
                    return f(desc, tables, iccc, mutatingScreenKind, insideContainer) as TDesc;
                }
            }
            return undefined;
        }

        public updateComponent(
            desc: TDesc,
            updates: Partial<TDesc>,
            tables: InputOutputTables,
            ccc: RewritingComponentConfiguratorContext,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): TDesc {
            let newDesc = super.updateComponent(desc, updates, tables, ccc, mutatingScreenKind);

            if (formTablePropertyDescriptor !== undefined) {
                const oldFormTables = this.getSubComponentTables(desc, tables, ccc, mutatingScreenKind)?.[0];
                const newFormTables = this.getSubComponentTables(newDesc, tables, ccc, mutatingScreenKind)?.[0];

                if (newFormTables?.output !== undefined && oldFormTables?.output !== newFormTables.output) {
                    newDesc = { ...newDesc, components: makeFormComponents(newFormTables.output, ccc.appKind) };
                }
            }

            return newDesc;
        }

        public inflate(ib: WireRowComponentInflationBackend, desc: TDesc): WireRowComponentHydrator | undefined {
            const {
                adc: { appKind },
                forBuilder,
            } = ib;

            if (inflator !== undefined) {
                return inflator(ib, desc as DescriptionOfComponent<TDesc>);
            }

            const subComponentDescriptions = this.getSubComponents(desc);

            let formOutputTable: TableGlideType | undefined;
            let componentIB: WireRowComponentInflationBackend;
            let columnAssignmentGetters: OutputValueGetters | undefined;
            let onSubmitHydrator: WireActionHydrator | undefined;
            if (formTablePropertyDescriptor === undefined) {
                componentIB = ib;
            } else {
                const formTables = this.getSubComponentTables(desc, ib.tables, ib.adc, ib.mutatingScreenKind)?.[0];
                if (formTables === undefined) return undefined;

                assert(formTables.input === ib.tables.input);
                formOutputTable = formTables.output;
                componentIB = ib.makeInflationBackendForTables({
                    ...formTables,
                    containingScreen: undefined,
                });
                const editedColumns = getEditedColumnsInFormComponents(
                    defined(subComponentDescriptions),
                    formTables,
                    ib.adc
                );
                columnAssignmentGetters = inflateColumnAssignmentsFromDescription(
                    ib,
                    desc as ColumnAssignmentsDescription,
                    getTableName(formOutputTable),
                    editedColumns
                );
                if (columnAssignmentGetters === undefined) return undefined;

                const onSubmitDescription = getActionProperty(asFormComponentDescription(desc).onSubmitAction);
                if (onSubmitDescription !== undefined) {
                    const onSubmitIB = ib.makeInflationBackendForTables({
                        ...makeInputOutputTables(formOutputTable),
                        containingScreen: undefined,
                    });
                    onSubmitHydrator = definedMap(onSubmitDescription, a => inflateActions(onSubmitIB, [a]));
                }
            }

            const getters = makeGetters(ib, spec.properties, desc, undefined);
            const hydrators = definedMap(subComponentDescriptions, cs =>
                mapFilterUndefined(cs, c => inflateComponent(componentIB, c, formOutputTable !== undefined))
            );

            return hb => {
                const hydratedGetters = hydrateGetters<any>(getters, hb, "");
                const hasDisplayedProperties = spec.properties.hasDisplayedProperties;
                // If a component doesn't have any displayed properties, it
                // means it should always display, like the Separator.
                if (!hydratedGetters[1] && hydrators === undefined && hasDisplayedProperties) return undefined;
                let hydrated = { kind, ...spreadComponentID(desc.componentID, forBuilder), ...hydratedGetters[0] };

                if (hydrators === undefined) {
                    return { component: hydrated, isValid: true, hasValue: false };
                }

                const { components, allValid, haveValues, componentsRowContext } = hb.hydrateSubComponents(
                    hydrators,
                    formOutputTable
                );
                let specialButtons: (WireButtonComponent | null)[] | undefined;
                if (formOutputTable !== undefined && componentsRowContext?.outputRow !== undefined) {
                    let action: WireAction | undefined;
                    if (allValid && haveValues) {
                        const hydratedColumnAssignments = hydrateOutputValueGetters(
                            hb,
                            defined(columnAssignmentGetters)
                        );
                        if (!isLoadingValue(hydratedColumnAssignments)) {
                            const outputRow: LoadedRow = {
                                ...removeLoadingValues(defined(componentsRowContext?.outputRow)),
                                ...hydratedColumnAssignments,
                            };
                            let onSubmitRunner: WireActionRunner | undefined;
                            if (onSubmitHydrator !== undefined) {
                                const onSubmitHB = hb.makeHydrationBackendForRow(
                                    outputRow,
                                    makeInputOutputTables(formOutputTable)
                                );
                                const onSubmitHydrated = hydrateAction(onSubmitHydrator, onSubmitHB, false);
                                if (!isLoadingValue(onSubmitHydrated)) {
                                    onSubmitRunner = onSubmitHydrated?.[0];
                                }
                            }
                            if (onSubmitHydrator === undefined || onSubmitRunner !== undefined) {
                                action = registerActionRunner(hb, "submitButton", () => [
                                    async (ab, handled) => {
                                        const success = await ab.addRow(
                                            getTableName(defined(formOutputTable)),
                                            outputRow
                                        );
                                        if (!success) return false;
                                        return (await onSubmitRunner?.(ab, handled)) ?? true;
                                    },
                                    undefined,
                                ]);
                            }
                        }
                    }
                    const submitButton: WireButtonComponent = {
                        kind: WireComponentKind.Button,
                        title: getLocalizedString("submit", appKind),
                        appearance: UIButtonAppearance.Filled,
                        action,
                    };
                    specialButtons = [submitButton];
                }

                hydrated = { ...hydrated, components, specialButtons } as WireComponent;

                if (formOutputTable !== undefined) {
                    // Form containers don't participate in validation
                    // themselves.
                    return { component: hydrated, isValid: true, hasValue: false };
                } else {
                    return { component: hydrated, isValid: allValid, hasValue: haveValues };
                }
            };
        }
    }

    return new Handler();
}

interface FormComponentDescription extends ColumnAssignmentsDescription, ComponentDescription {
    readonly targetTable: PropertyDescription | undefined;
    readonly onSubmitAction: PropertyDescription | undefined;
}

function formPropertyName(name: string): string {
    return `form${upperFirst(name)}`;
}

function makeFormComponentHandler<TDesc extends FormComponentDescription>(
    spec: FluentComponentSpec
): ComponentHandler<TDesc> {
    const {
        properties: { propertySpecs, arraySpecs, actionSpecs },
        formProperties: { propertySpecs: formPropertySpecs },
        displayName,
        kind,
        group = "Forms",
        description = "No description",
        icon = "componentTitle",
        docURL,
        featureSetting,
    } = spec;
    assert(kind !== undefined && displayName !== undefined);
    assert(spec.containerKind === undefined);

    const formPropertyHandlers = new Map(
        formPropertySpecs.map(p => {
            const name = p.argumentSpec[0];
            const flags = [ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp, ColumnPropertyFlag.Optional];
            if (p.emptyByDefault) {
                flags.push(ColumnPropertyFlag.EmptyByDefault);
            }
            const handler = new ColumnPropertyHandler(
                formPropertyName(name),
                startCase(name),
                flags,
                getTargetTable,
                [name],
                getPrimitiveColumns,
                p.preferredType ?? "string",
                PropertySection.Data
            );
            return [name, handler];
        })
    );
    const preferredTypeForProperty = new Map(
        formPropertySpecs.map(p => {
            const name = p.argumentSpec[0];
            return [name, p.preferredType];
        })
    );

    // returns (form property name -> target column name)
    function getAssignedFormColumns(desc: Description | undefined): ReadonlyMap<string, string> {
        const map = new Map<string, string>();
        if (desc !== undefined) {
            for (const [n, h] of formPropertyHandlers) {
                const c = h.getColumnName(desc);
                if (c === undefined) continue;
                map.set(n, c);
            }
        }
        return map;
    }

    class Handler extends ComponentHandlerBase<TDesc> {
        constructor() {
            super(kind);
        }

        public get appKinds(): AppKind | "both" {
            return AppKind.Page;
        }

        public getActionDescriptors(
            desc: TDesc,
            _table: TableGlideType | undefined,
            schema: SchemaInspector | undefined,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): readonly ActionPropertyDescriptor[] {
            return [
                ...makeActionDescriptors(
                    undefined,
                    desc,
                    actionSpecs,
                    getTargetTable,
                    schema,
                    mutatingScreenKind,
                    false
                ),
                makeOnSubmitActionDescriptor(schema, mutatingScreenKind),
            ];
        }

        public getDescriptor(
            desc: TDesc | undefined,
            table: TableGlideType | undefined,
            ccc: AppDescriptionContext,
            mutatingScreenKind: MutatingScreenKind | undefined
        ): ComponentDescriptor {
            const targetTableName = getTargetTableName(desc);
            const assignedColumnNames = getAssignedFormColumns(desc).values();
            const [columnAssignmentDescriptors] = makeTargetPropertyDescriptors(
                targetTableName,
                table,
                ccc,
                new Set(assignedColumnNames)
            );

            return {
                name: defined(displayName),
                description,
                img: icon,
                group,
                helpUrl: getDocURL(docURL ?? "button"),
                isLegacy: featureSetting !== undefined && !getFeatureSetting(featureSetting),
                properties: [
                    ...this.getBasePropertyDescriptors(),
                    ...makePropertyDescriptors(
                        desc,
                        undefined,
                        [...propertySpecs, ...arraySpecs],
                        undefined,
                        ccc,
                        mutatingScreenKind,
                        false,
                        false
                    ),
                    targetTablePropertyHandler,
                    ...formPropertyHandlers.values(),
                    ...columnAssignmentDescriptors,
                ],
            };
        }

        public getEditedColumns(
            desc: TDesc,
            tables: InputOutputTables,
            adc: AppDescriptionContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            withActions: boolean
        ): EditedColumnsAndTables {
            const base = super.getEditedColumns(desc, tables, adc, mutatingScreenKind, withActions);

            const tableName = getTableProperty(desc.targetTable);
            if (tableName === undefined) return base;
            const assignedColumnNames = Array.from(getAssignedFormColumns(desc).values());

            const columnAssignments = getColumnAssignments(desc);
            return combineEditedColumnsAndTables(base, {
                editedColumns: [
                    ...columnAssignments.map(a => [a.destColumn, false, true, tableName] as const),
                    ...assignedColumnNames.map(c => [c, false, true, tableName] as const),
                ],
                deletedTables: [],
            });
        }

        public inflate?(ib: WireInflationBackend, desc: TDesc): WireRowComponentHydrator | undefined {
            const {
                adc: { appKind },
                forBuilder,
            } = ib;

            const targetTableName = getTableProperty(desc?.targetTable);
            if (targetTableName === undefined) return undefined;
            const targetTable = ib.adc.findTable(targetTableName);
            if (targetTable === undefined) return undefined;

            const getters = makeGetters(ib, spec.properties, desc, undefined);
            const assignedColumnNames = getAssignedFormColumns(desc);
            const onSubmitDescription = getActionProperty(desc.onSubmitAction);
            const onSubmitIB = ib.makeInflationBackendForTables({
                ...makeInputOutputTables(targetTable),
                containingScreen: undefined,
            });
            const onSubmitHydrator = definedMap(onSubmitDescription, a => inflateActions(onSubmitIB, [a]));

            function getFormValue(hb: WireRowComponentHydrationBackend, name: string): WireEditableValue<string> {
                return hb.getState(`input-${name}`, (x): x is string => typeof x === "string", "");
            }

            const columnAssignmentGetters = inflateColumnAssignmentsFromDescription(
                ib,
                desc,
                targetTableName,
                new Set(assignedColumnNames.values())
            );

            const outputValueGetters: OutputValueGetters = [
                ...(columnAssignmentGetters ?? []),
                ...Array.from(assignedColumnNames).map(
                    // TODO: We're getting the form properties twice - here and below in `formProps`.
                    ([f, o]): [string, WireRowComponentValueGetter] => [
                        o,
                        (hb: WireRowComponentHydrationBackend) => getFormValue(hb, f).value,
                    ]
                ),
            ];

            if (outputValueGetters === undefined) return undefined;

            return hb => {
                // TODO: Obey the `hasDisplayedProperties` of the specs and
                // always display if it's `false`.
                let isValid = true;
                const formProps: [string, WireEditableValue<string>][] = Array.from(assignedColumnNames.keys()).map(
                    name => {
                        let ev = getFormValue(hb, name);
                        const preferredType = preferredTypeForProperty.get(name);
                        if (ev.value === "") {
                            isValid = false;
                        }
                        if (preferredType === "email-address") {
                            if (ev.value !== "" && !isValidEmailAddress(ev.value)) {
                                isValid = false;
                                ev = { ...ev, error: getLocalizedString("pleaseEnterValidEmail", appKind) };
                            }
                        }
                        return [name, ev];
                    }
                );
                // TODO: We should be adding this as an invisible row, but to
                // do that it'd have to be persistent between hydrations.  We
                // could use the `isFirstHydration` to make the row, add it as
                // invisible, and store its row ID as the state.  Or better,
                // we use the same mechanism the form container is using,
                // which would mean that we wouldn't even need to store the
                // state of the input components.
                const outputRow: LoadedRow = {
                    ...hydrateOutputValueGetters(hb, outputValueGetters),
                    $rowID: makeRowID(),
                    $isVisible: false,
                };
                const onSubmitHB = hb.makeHydrationBackendForRow(outputRow, makeInputOutputTables(targetTable));
                const onSubmitHydrated = hydrateAction(onSubmitHydrator, onSubmitHB, false);
                let submitAction: WireAction | undefined;
                if (isValid && !isLoadingValue(onSubmitHydrated)) {
                    submitAction = {
                        token: hb.registerAction(async (ab, handled) => {
                            // Clear the form first
                            for (const [, { onChangeToken }] of formProps) {
                                if (onChangeToken === undefined) continue;
                                ab.valueChanged(onChangeToken, "");
                            }

                            const success = await ab.addRow(targetTableName, outputRow);
                            if (!success) return false;
                            return (await onSubmitHydrated?.[0](ab, handled)) ?? true;
                        }),
                    };
                }
                return {
                    component: {
                        kind,
                        ...spreadComponentID(desc.componentID, forBuilder),
                        submitAction,
                        ...hydrateGetters<any>(getters, hb, "")[0],
                        ...fromPairs(formProps),
                    } as WireComponent,
                    isValid: true,
                    hasValue: false,
                };
            };
        }
    }

    return new Handler();
}

export function makeFluentComponentUI(
    { spec }: FluentComponent<unknown, unknown, unknown>,
    componentName: string
): UIClass {
    const {
        properties: { propertySpecs },
    } = spec;
    class ComponentUI extends ReactUI {
        static get spec(): DeserializationSpec<"ui"> {
            return [componentName, "ui", ...propertySpecs.map(p => p.argumentSpec)];
        }
    }
    return ComponentUI;
}

// TODO: Don't use `any`
export function makeFluentComponentHandler<TProps, TForm, TWire>(
    component: FluentComponent<TProps, TForm, TWire>,
    inflator?: ComponentInflator<FluentComponent<TProps, TForm, TWire>>
): ComponentHandler<any> {
    const { spec } = component;

    if (spec.formProperties.propertySpecs.length === 0) {
        return makeComponentHandler(spec, inflator);
    } else {
        assert(inflator === undefined);
        return makeFormComponentHandler(spec);
    }
}

export type ArrayContentInflator<TProps> = (
    ib: WireInflationBackend,
    desc: ArrayContentDescriptionForProperties<TProps>,
    componentID: string | undefined
) => WireTableComponentHydrator | undefined;

export function makeFluentArrayContentHandler<TItemProps, TComponentProps>(
    { spec }: FluentArrayContent<TItemProps, TComponentProps, unknown>,
    inflator?: ArrayContentInflator<TComponentProps & TItemProps>
): ArrayContentHandler<ArrayContentDescriptionForProperties<TComponentProps & TItemProps>> {
    const {
        displayName,
        format,
        specialCases,
        itemProperties: { propertySpecs: itemPropertySpecs, arraySpecs: itemArraySpecs, actionSpecs: itemActionSpecs },
        componentProperties: {
            propertySpecs: componentPropertySpecs,
            arraySpecs: componentArraySpecs,
            actionSpecs: componentActionSpecs,
        },
    } = spec;
    // TODO: See whether we're actually using this - the way we get searchable
    // components in Pages is separate from this.
    const searchProperties = itemPropertySpecs.filter(p => p.isSearchable).map(p => p.argumentSpec[0]);

    class Handler extends ArrayContentHandlerBase<ArrayContentDescriptionForProperties<TComponentProps & TItemProps>> {
        public needsCaption(): PropertySection | undefined {
            return undefined;
        }

        public get supportsDynamicFilter(): boolean {
            return true;
        }

        public getSpecialCaseDescriptors(): readonly ComponentSpecialCaseDescriptor[] {
            return specialCases?.map(([c]) => c) ?? [];
        }

        public getBasicSearchProperties(): readonly string[] {
            return searchProperties;
        }

        public makeContentForProcessedArray(builder: ComponentBuilder): SerializedComponent {
            // We don't support these for the old component model
            return builder.b.emptyUI;
        }

        public get defaultPageSize(): number | undefined {
            return 12;
        }

        public get sourcePropertyLabel(): [string, PropertySection] {
            return ["Source", PropertySection.DataTop];
        }

        public getContentPropertyDescriptors<
            T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>
        >(
            getPropertyTable: PropertyTableGetter | undefined,
            insideInlineList: boolean,
            desc: T | undefined,
            ccc: AppDescriptionContext,
            mutatingScreenKind: MutatingScreenKind | undefined,
            isDefaultArrayScreen: boolean,
            withTransforms: boolean
        ): readonly PropertyDescriptor[] {
            const superProps = super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                undefined
            );
            const componentProps = mapFilterUndefined([...componentPropertySpecs, ...componentArraySpecs], p =>
                p.descriptorConstructor(desc, undefined, undefined, ccc, mutatingScreenKind, false, false)
            );
            const itemProps = mapFilterUndefined([...itemPropertySpecs, ...itemArraySpecs], p =>
                p.descriptorConstructor(desc, desc, getPropertyTable, ccc, mutatingScreenKind, false, true)
            );
            return [...superProps, ...componentProps, ...itemProps];
        }

        public getActionDescriptors<T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>>(
            desc: T,
            getIndirectTable: PropertyTableGetter | undefined,
            schema: SchemaInspector | undefined
        ): readonly ActionPropertyDescriptor[] {
            return [
                ...makeActionDescriptors(
                    desc,
                    undefined,
                    componentActionSpecs ?? [],
                    undefined,
                    schema,
                    undefined,
                    false
                ),
                ...makeActionDescriptors(
                    desc,
                    undefined,
                    itemActionSpecs ?? [],
                    getIndirectTable,
                    schema,
                    undefined,
                    true
                ),
            ];
        }

        public defaultContentDescription<TBase>(
            baseDesc: TBase,
            tables: InputOutputTables,
            ccc: AppDescriptionContext,
            insideInlineList: boolean,
            mutatingScreenKind: MutatingScreenKind | undefined,
            isDefaultArrayScreen: boolean,
            getIndirectTable: PropertyTableGetter | undefined,
            specialCaseDescriptor: ComponentSpecialCaseDescriptor | undefined
        ): (TBase & ArrayContentDescriptionForProperties<TComponentProps & TItemProps>) | undefined {
            const desc = super.defaultContentDescription(
                baseDesc,
                tables,
                ccc,
                insideInlineList,
                mutatingScreenKind,
                isDefaultArrayScreen,
                getIndirectTable,
                specialCaseDescriptor
            );
            if (desc === undefined) return undefined;

            const populated = populateDescription(
                d => this.getActionDescriptors(d, getIndirectTable, ccc),
                PopulationMode.Default,
                desc,
                desc,
                tables,
                ccc,
                false,
                undefined
            );
            if (populated === undefined) return undefined;
            if (specialCaseDescriptor === undefined) {
                return populated;
            } else {
                const itemTable = getIndirectTable?.(tables, desc, ccc);
                const [, specializer] = defined(specialCases.find(([c]) => c === specialCaseDescriptor));
                return specializer(populated as any, tables, itemTable, ccc, mutatingScreenKind, false) as any;
            }
        }

        public getDescriptiveName(
            _desc: ArrayContentDescriptionForProperties<TComponentProps & TItemProps>,
            _caption: string | undefined,
            _inlineListHostTable: TableGlideType | undefined,
            propertyDisplayName: string | undefined
        ): [string, string] | undefined {
            if (displayName === undefined) return undefined;
            return [displayName, propertyDisplayName ?? ""];
        }

        public inflateContent<T extends ArrayContentDescriptionForProperties<TComponentProps & TItemProps>>(
            ib: WireInflationBackend,
            desc: T,
            _captionGetter: WireStringGetter | undefined,
            containingRowIB: WireRowComponentInflationBackend | undefined,
            componentID: string | undefined
        ): WireTableComponentHydrator | undefined {
            if (inflator !== undefined) {
                return inflator(ib, desc, componentID);
            }

            const { forBuilder } = ib;

            const componentGetters = makeGetters(defined(containingRowIB), spec.componentProperties, desc, undefined);
            // ##whenInArrayComponentItems:
            // TODO: We do an ugly thing here where we pretend that the item
            // description's parent is the component description, when in
            // reality they're the same.  To fix it maybe we want to add a
            // `TExtraProps` argument to `FluentProperties` which gets added
            // to the first argument for the `when`.
            const itemGetters = makeGetters(ib, spec.itemProperties, desc, desc);

            // To make TS happy
            assert(format !== ArrayScreenFormat.DataGrid);

            return (thb, chb) => {
                const data = thb.tableScreenContext;
                if (data === undefined) return undefined;

                return {
                    component: {
                        kind: WireComponentKind.List,
                        ...spreadComponentID(componentID, forBuilder),
                        format: defined(format),
                        helpPath: spec.docURL,
                        componentTitle: "",
                        items: data.map(row => {
                            const rhb = thb.makeHydrationBackendForRow(row);
                            return hydrateGetters<any>(itemGetters, rhb, `item[${row.$rowID}].`)[0];
                        }),
                        ...definedMap(chb, b => hydrateGetters<any>(componentGetters, b, "component.")[0]),
                    },
                    isValid: true,
                    hasValue: false,
                };
            };
        }
    }

    return new Handler(defined(format));
}
